"use client";

import { memo } from "react";
import type { KundaliTranslations } from "./KundaliFormCard.i18n";
import { FieldError, FieldWrapper } from "./KundaliFormCard.fields";
import { PhoneInputE164 } from "@/components/PhoneInputE164";

/**
 * Contact fields for the chat-entry / kundali form (India-default, E.164):
 *   - PhoneField   — phone is the strongest identity/delivery signal but is
 *                    OPTIONAL (owner decision 2026-09-24); validated only when
 *                    non-empty. India stays the default and keeps its 10-digit
 *                    +91 UX; the country picker exists because real diaspora
 *                    buyers hold +971/+1/+60 numbers. Numeric tel keypad and an
 *                    honest transactional save reason.
 *   - EmailField   — always optional; validated only when non-empty.
 * Both persist via the existing captureReadingContact helper after the reading
 * is created (the form is the single decision — these are part of it, not a
 * second CTA, rule 39). The reading always renders regardless (rule 46).
 */

interface PhoneFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  t: KundaliTranslations;
  required: boolean;
  error?: string;
}

export const PhoneField = memo(function PhoneField({
  value,
  onChange,
  onFocus,
  t,
  required,
  error,
}: PhoneFieldProps) {
  return (
    <FieldWrapper>
      <label
        htmlFor="phone"
        className="block text-body-small font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.phone}
        {!required && (
          <>
            {" "}
            <span className="font-normal text-text-muted">{t.optional}</span>
          </>
        )}
      </label>
      <PhoneInputE164
        id="phone"
        name="phone"
        required={required}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={t.phonePlaceholder}
        countryLabel={t.phoneCountryLabel}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "phone-error" : "phone-reason"}
      />
      <p
        id="phone-reason"
        className="mt-1.5 text-[11px]"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {t.phoneReason}
      </p>
      <FieldError id="phone-error" message={error} />
    </FieldWrapper>
  );
});

interface EmailFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  t: KundaliTranslations;
  error?: string;
}

export const EmailField = memo(function EmailField({
  value,
  onChange,
  onFocus,
  t,
  error,
}: EmailFieldProps) {
  return (
    <FieldWrapper>
      <label
        htmlFor="email"
        className="block text-body-small font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.email}{" "}
        <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
          {t.optional}
        </span>
      </label>
      <input
        id="email"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={t.emailPlaceholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "email-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-body"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="email-error" message={error} />
    </FieldWrapper>
  );
});
